// PlantillasReportesManager.js - CON PERMISOS, FAVORITOS, COMPARTIR Y SNACKBAR
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    IconButton,
    Paper,
    Chip,
    Tabs,
    Tab,
    Tooltip,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Snackbar,
    Popper,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    FileCopy as CopyIcon,
    Share as ShareIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Visibility as PreviewIcon,
    Public as PublicIcon,
    Lock as LockIcon,
    Person as PersonIcon,
    Save as SaveIcon,
    ExitToApp as ExitToAppIcon,
} from '@mui/icons-material';

import {
    plantillasListarJWT,
    plantillasGuardarJWT,
    plantillasEliminarJWT,
    plantillasFavoritoJWT,
    plantillasCompartidosGetJWT,
    plantillasCompartidosSetJWT,
    plantillasUnshareJWT,
    plantillasBuscarPersonalJWT,
} from '../../../api/endpoints';

// Hook para debounce
function useDebounced(value, delay) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay || 350);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        minHeight: '80vh',
        maxHeight: '90vh'
    },
    header: {
        background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
        color: 'white',
        padding: theme.spacing(2, 3)
    },
    plantillaCard: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
        borderRadius: 4,
        border: '1px solid #e0e0e0',
        transition: 'all 0.2s',
        '&:hover': {
            boxShadow: theme.shadows[3],
            borderColor: '#1976d2'
        }
    },
    campoItem: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        borderRadius: 4,
        backgroundColor: '#f5f5f5',
        border: '1px solid #e0e0e0',
        cursor: 'move'
    },
    campoItemSelected: {
        backgroundColor: '#e3f2fd',
        borderColor: '#1976d2'
    },
    grupoHeader: {
        fontWeight: 600,
        color: '#1976d2',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        fontSize: 12
    }
}));

// Definición fallback de campos disponibles (se usa si no se pasa prop camposDisponibles)
const CAMPOS_DISPONIBLES_DEFAULT = [
    { id: 'Caratula', label: 'Carátula', grupo: 'Básicos', ancho: 200 },
    { id: 'Autos', label: 'Autos/Expediente', grupo: 'Básicos', ancho: 120 },
    { id: 'Estado', label: 'Estado', grupo: 'Básicos', ancho: 100 },
    { id: 'TipoProceso', label: 'Tipo de Proceso', grupo: 'Básicos', ancho: 120 },
    { id: 'Radicacion', label: 'Radicación', grupo: 'Ubicación', ancho: 200 },
    { id: 'Juzgado', label: 'Juzgado', grupo: 'Ubicación', ancho: 150 },
    { id: 'Fuero', label: 'Fuero', grupo: 'Ubicación', ancho: 120 },
    { id: 'Jurisdiccion', label: 'Jurisdicción', grupo: 'Ubicación', ancho: 100 },
    { id: 'FechaInicio', label: 'Fecha Inicio', grupo: 'Fechas', ancho: 90 },
    { id: 'FechaRadicacion', label: 'Fecha Radicación', grupo: 'Fechas', ancho: 90 },
    { id: 'UltimoMovimiento', label: 'Último Mov.', grupo: 'Fechas', ancho: 90 },
    { id: 'FechaAgregado', label: 'Fecha Agregado', grupo: 'Fechas', ancho: 90 },
    { id: 'Abogados', label: 'Abogado(s)', grupo: 'Personas', ancho: 150 },
    { id: 'Actor', label: 'Actor', grupo: 'Partes', ancho: 150 },
    { id: 'Demandado', label: 'Demandado', grupo: 'Partes', ancho: 150 },
    { id: 'CUIT', label: 'CUIT', grupo: 'Partes', ancho: 100 },
    { id: 'MontoCapital', label: 'Capital', grupo: 'Económico', ancho: 100 },
    { id: 'MontoInteres', label: 'Interés', grupo: 'Económico', ancho: 100 },
    { id: 'MontoTotal', label: 'Monto Total', grupo: 'Económico', ancho: 100 },
    { id: 'Observaciones', label: 'Observaciones', grupo: 'Otros', ancho: 200 },
    { id: 'Notas', label: 'Notas', grupo: 'Otros', ancho: 150 },
    { id: 'Prioridad', label: 'Prioridad', grupo: 'Otros', ancho: 80 }
];

// Helper: convertir formato {categoria: [campos]} a flat array con grupo
const convertirCamposAgrupadosAFlat = (camposAgrupados) => {
    if (!camposAgrupados || typeof camposAgrupados !== 'object') return CAMPOS_DISPONIBLES_DEFAULT;
    // Si ya es un array, asumimos formato flat
    if (Array.isArray(camposAgrupados)) return camposAgrupados;
    // Convertir de {grupo: [campos]} a flat array con propiedad grupo
    const resultado = [];
    for (const [grupo, campos] of Object.entries(camposAgrupados)) {
        if (Array.isArray(campos)) {
            for (const campo of campos) {
                resultado.push({ ...campo, grupo });
            }
        }
    }
    return resultado.length > 0 ? resultado : CAMPOS_DISPONIBLES_DEFAULT;
};

const PlantillasReportesManager = ({
    open,
    onClose,
    onSelect,
    usuario,
    permisos,
    configActual,
    abrirEditor: abrirEditorProp,
    categoriaDefault,
    camposDisponibles: camposDisponiblesProp
}) => {
    const classes = useStyles();

    // Campos disponibles: usar prop si se pasa, sino el default interno
    const CAMPOS_DISPONIBLES = React.useMemo(() => {
        if (camposDisponiblesProp) {
            return convertirCamposAgrupadosAFlat(camposDisponiblesProp);
        }
        return CAMPOS_DISPONIBLES_DEFAULT;
    }, [camposDisponiblesProp]);

    const CAMPOS_POR_GRUPO = React.useMemo(() => {
        return CAMPOS_DISPONIBLES.reduce((acc, campo) => {
            if (!acc[campo.grupo]) acc[campo.grupo] = [];
            acc[campo.grupo].push(campo);
            return acc;
        }, {});
    }, [CAMPOS_DISPONIBLES]);

    // ===== SNACKBAR =====
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
    const openSnack = (message, severity = 'info') => setSnack({ open: true, message, severity });
    const closeSnack = () => setSnack(prev => ({ ...prev, open: false }));

    // Calcular permisos para plantillas
    const permisosPlantillas = React.useMemo(() => {
        if (!permisos) return { puedeCrearGlobal: false, puedeVerTodos: false };

        // Unwrap permisos (igual que Documentos.js)
        const permisosModulos = permisos?.permisos || permisos;

        let puedeCrearGlobal = false;
        let puedeVerTodos = false;

        for (const nombreModulo in permisosModulos) {
            const moduloPermisos = permisosModulos[nombreModulo];
            const bibPermisos = moduloPermisos?.["BIBLIOTECA DE DOCUMENTOS RESTRINGIDOS"];

            if (bibPermisos?.["INSERT"] === true) {
                puedeCrearGlobal = true;
            }
            if (bibPermisos?.["SELECT"] === true) {
                puedeVerTodos = true;
            }
        }

        return { puedeCrearGlobal, puedeVerTodos };
    }, [permisos]);

    // Estados
    const [tab, setTab] = useState(0);
    const [plantillas, setPlantillas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Editor de plantilla
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingPlantilla, setEditingPlantilla] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        campos: ['Caratula', 'Autos', 'Estado'],
        agrupacion: '',
        incluirResumen: true,
        incluirGrafico: true,
        orientacion: 'portrait',
        scope: 'PRIVATE'
    });
    const [saving, setSaving] = useState(false);

    // ===== ELIMINAR =====
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [plantillaToDelete, setPlantillaToDelete] = useState(null);

    // ===== COMPARTIR (por Id de Personal) =====
    const [shareOpen, setShareOpen] = useState(false);
    const [sharePlantilla, setSharePlantilla] = useState(null);
    const [shareSelectedIds, setShareSelectedIds] = useState([]);
    const [shareQuery, setShareQuery] = useState('');
    const shareQueryDeb = useDebounced(shareQuery, 350);
    const [shareOptions, setShareOptions] = useState([]);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareSearchLoading, setShareSearchLoading] = useState(false);
    const [shareSaving, setShareSaving] = useState(false);
    const [shareNameMap, setShareNameMap] = useState({});

    // ===== DESCOMPARTIR CONMIGO =====
    const [unshareConfirmOpen, setUnshareConfirmOpen] = useState(false);
    const [plantillaToUnshare, setPlantillaToUnshare] = useState(null);


    const shareOptionsComputed = React.useMemo(() => {
        const map = new Map(shareOptions.map(o => [String(o.id), o]));
        for (const id of shareSelectedIds) {
            const k = String(id);
            if (!map.has(k)) map.set(k, { id, nombre: shareNameMap[id] || String(id) });
        }
        return Array.from(map.values());
    }, [shareOptions, shareSelectedIds, shareNameMap]);

    // ===== BUSCADOR DE USUARIOS =====
    const fetchPersonal = useCallback(async (q) => {
        if (!q || q.length < 2) { setShareOptions([]); return; }
        setShareSearchLoading(true);
        try {
            const res = await plantillasBuscarPersonalJWT(q);
            if (res?.ok) {
                const items = (res.items || []).map(x => ({
                    id: Number(x.id),
                    nombre: String(x.nombre || "").trim(),
                })).filter(x => Number.isFinite(x.id) && x.nombre);

                setShareOptions(items);

                setShareNameMap(prev => {
                    const next = { ...prev };
                    for (const it of items) if (it.nombre) next[it.id] = it.nombre;
                    return next;
                });
            } else {
                setShareOptions([]);
            }
        } catch {
            setShareOptions([]);
        } finally {
            setShareSearchLoading(false);
        }
    }, []);

    useEffect(() => { fetchPersonal(shareQueryDeb); }, [shareQueryDeb, fetchPersonal]);

    // Cargar plantillas
    const cargarPlantillas = useCallback(async () => {
        setLoading(true);
        try {
            const tabs = ['todos', 'mios', 'compartidos', 'favoritos'];
            const res = await plantillasListarJWT(tabs[tab]);
            let items = res?.items || [];
            // Tab "Públicas" (0): solo mostrar GLOBAL
            if (tab === 0) {
                items = items.filter(p => (p.Scope || '').toUpperCase() === 'GLOBAL');
            }
            setPlantillas(items);
        } catch (error) {
            console.error('Error cargando plantillas:', error);
            openSnack('Error al cargar plantillas', 'error');
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        if (open) {
            cargarPlantillas();
        }
    }, [open, cargarPlantillas]);

    // Filtrar plantillas
    const plantillasFiltradas = plantillas.filter(p => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return p.Nombre.toLowerCase().includes(q) ||
            (p.Descripcion || '').toLowerCase().includes(q) ||
            (p.Categoria || '').toLowerCase().includes(q);
    });

    useEffect(() => {
        if (open && !permisosPlantillas.puedeVerTodos && tab === 0) {
            setTab(1); // saltar a "Mis Plantillas"
        }
    }, [open, permisosPlantillas.puedeVerTodos, tab]);

    const puedeRealizarAccion = React.useCallback((plantilla, accion) => {
        if (!plantilla) return false;

        const tabsKeys = ['todos', 'mios', 'compartidos', 'favoritos'];
        const tabActual = tabsKeys[tab] || 'todos';

        // Owner "real", comparando case-insensitive
        const usuarioStr = usuario ? String(usuario).toLowerCase() : '';
        const ownerStr = String(plantilla.OwnerUsuario || '').toLowerCase();
        const esOwnerReal = !!usuarioStr && usuarioStr === ownerStr;

        const esGlobal = (plantilla.Scope || plantilla.scope) === 'GLOBAL';
        const esCompartido = !!(plantilla.SharedWithMe ?? plantilla.sharedWithMe);

        // =====================
        // TAB: Mis plantillas
        // =====================
        // El back ya filtró por OwnerUsuario = @Usuario, acá asumimos que TODAS son mías.
        if (tabActual === 'mios') {
            if (accion === 'unshare-self') return false;     // no tiene sentido acá
            if (accion === 'share' && esGlobal) return false; // global no se comparte por usuario
            return ['edit', 'delete', 'share', 'favorite', 'duplicate', 'use'].includes(accion);
        }

        // =====================
        // TAB: Compartidas
        // =====================
        if (tabActual === 'compartidos') {
            if (esOwnerReal) {
                // Yo la creé y además está compartida
                if (accion === 'unshare-self') return false;
                return ['edit', 'delete', 'share', 'favorite', 'duplicate', 'use'].includes(accion);
            } else {
                // Me la compartieron
                if (accion === 'delete' || accion === 'share') return false;
                if (accion === 'unshare-self') return true;   // puedo dejar de verla
                return ['favorite', 'duplicate', 'use'].includes(accion);
            }
        }

        // =====================
        // TAB: Favoritas
        // =====================
        if (tabActual === 'favoritos') {
            if (accion === 'unshare-self') return false; // se hace desde "Compartidas"

            if (esOwnerReal) {
                return ['edit', 'delete', 'share', 'favorite', 'duplicate', 'use'].includes(accion);
            }

            if (esCompartido) {
                if (accion === 'delete' || accion === 'share') return false;
                return ['favorite', 'duplicate', 'use'].includes(accion);
            }
        }

        // =====================
        // TAB: Todas
        // =====================
        if (tabActual === 'todos') {
            if (esOwnerReal) {
                if (accion === 'unshare-self') return false;
                if (accion === 'share' && esGlobal) return false; // global no usa compartir por usuario
                return ['edit', 'delete', 'share', 'favorite', 'duplicate', 'use'].includes(accion);
            }

            // No soy owner, pero es GLOBAL: miramos permisos de biblioteca restringida
            if (esGlobal) {
                if (!permisos) return false;

                const mapAccion = {
                    use: 'SELECT',
                    favorite: 'SELECT',
                    duplicate: 'SELECT',
                    share: 'SELECT',
                    edit: 'UPDATE',
                    delete: 'DELETE',
                };
                const permisoRequerido = mapAccion[accion];
                if (!permisoRequerido) return false;

                const pm = permisos?.permisos || permisos;
                for (const nombreModulo in pm) {
                    const moduloPermisos = pm[nombreModulo];
                    const bibPermisos = moduloPermisos?.['BIBLIOTECA DE DOCUMENTOS RESTRINGIDOS'];
                    if (bibPermisos?.[permisoRequerido] === true) {
                        return true;
                    }
                }
                return false;
            }

            // Por si se colara alguna privada compartida
            if (esCompartido) {
                if (accion === 'delete' || accion === 'share' || accion === 'unshare-self') return false;
                return ['favorite', 'duplicate', 'use'].includes(accion);
            }

            return false;
        }

        // =====================
        // Fallbacks generales
        // =====================

        // Plantilla privada mía (por fuera de tabs esperados)
        if (esOwnerReal && !esGlobal) {
            if (accion === 'unshare-self') return false;
            return ['edit', 'delete', 'share', 'favorite', 'duplicate', 'use'].includes(accion);
        }

        // Compartida conmigo
        if (esCompartido) {
            if (accion === 'delete' || accion === 'share' || accion === 'unshare-self') return false;
            return ['favorite', 'duplicate', 'use'].includes(accion);
        }

        // Global no mía: volvemos a chequear permisos
        if (esGlobal && permisos) {
            const mapAccion = {
                use: 'SELECT',
                favorite: 'SELECT',
                duplicate: 'SELECT',
                share: 'SELECT',
                edit: 'UPDATE',
                delete: 'DELETE',
            };
            const permisoRequerido = mapAccion[accion];
            if (!permisoRequerido) return false;

            const pm2 = permisos?.permisos || permisos;
            for (const nombreModulo in pm2) {
                const moduloPermisos = pm2[nombreModulo];
                const bibPermisos = moduloPermisos?.['BIBLIOTECA DE DOCUMENTOS RESTRINGIDOS'];
                if (bibPermisos?.[permisoRequerido] === true) {
                    return true;
                }
            }
        }

        return false;
    }, [usuario, tab, permisos]);

    // Si se pide abrir el editor directamente (desde "Guardar plantilla")
    React.useEffect(() => {
        if (open && abrirEditorProp) {
            handleNueva();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, abrirEditorProp]);


    // Abrir editor para nueva plantilla
    const handleNueva = () => {
        setEditingPlantilla(null);
        setFormData({
            nombre: configActual?.titulo || '',
            descripcion: '',
            campos: configActual?.campos?.length > 0 ? configActual.campos : ['Caratula', 'Autos', 'Estado'],
            agrupacion: '',
            incluirResumen: configActual?.incluirResumen ?? true,
            incluirGrafico: configActual?.incluirGrafico ?? true,
            orientacion: configActual?.orientacion || 'portrait',
            scope: 'PRIVATE',
            categoria: categoriaDefault || 'Judiciales'
        });
        setEditorOpen(true);
    };

    // Abrir editor para editar
    const handleEditar = (plantilla) => {
        setEditingPlantilla(plantilla);
        try {
            const config = JSON.parse(plantilla.Configuracion || '{}');
            setFormData({
                nombre: plantilla.Nombre,
                descripcion: plantilla.Descripcion || '',
                campos: config.campos || ['Caratula', 'Autos', 'Estado'],
                agrupacion: config.agrupacion || '',
                incluirResumen: config.incluirResumen !== false,
                incluirGrafico: config.incluirGrafico !== false,
                orientacion: config.orientacion || 'portrait',
                scope: plantilla.Scope || 'PRIVATE',
                categoria: plantilla.Categoria || categoriaDefault || 'Judiciales'
            });
        } catch {
            setFormData({
                nombre: plantilla.Nombre,
                descripcion: plantilla.Descripcion || '',
                campos: ['Caratula', 'Autos', 'Estado'],
                agrupacion: '',
                incluirResumen: true,
                incluirGrafico: true,
                orientacion: 'portrait',
                scope: plantilla.Scope || 'PRIVATE',
                categoria: plantilla.Categoria || categoriaDefault || 'Judiciales'
            });
        }
        setEditorOpen(true);
    };

    // Duplicar plantilla
    const handleDuplicar = (plantilla) => {
        setEditingPlantilla(null);
        try {
            const config = JSON.parse(plantilla.Configuracion || '{}');
            setFormData({
                nombre: `${plantilla.Nombre} (copia)`,
                descripcion: plantilla.Descripcion || '',
                campos: config.campos || ['Caratula', 'Autos', 'Estado'],
                agrupacion: config.agrupacion || '',
                incluirResumen: config.incluirResumen !== false,
                incluirGrafico: config.incluirGrafico !== false,
                orientacion: config.orientacion || 'portrait',
                scope: 'PRIVATE'
            });
        } catch {
            setFormData({
                nombre: `${plantilla.Nombre} (copia)`,
                descripcion: '',
                campos: ['Caratula', 'Autos', 'Estado'],
                agrupacion: '',
                incluirResumen: true,
                incluirGrafico: true,
                orientacion: 'portrait',
                scope: 'PRIVATE'
            });
        }
        setEditorOpen(true);
    };

    // Guardar plantilla
    const handleGuardar = async () => {
        if (!formData.nombre.trim()) {
            openSnack('El nombre es requerido', 'warning');
            return;
        }
        if (formData.campos.length === 0) {
            openSnack('Debe seleccionar al menos un campo', 'warning');
            return;
        }

        // Verificar permisos para GLOBAL
        if (formData.scope === 'GLOBAL' && !permisosPlantillas.puedeCrearGlobal) {
            openSnack('No tiene permisos para crear plantillas globales', 'error');
            return;
        }

        setSaving(true);
        try {
            const configuracion = {
                titulo: formData.nombre,
                campos: formData.campos,
                agrupacion: formData.agrupacion,
                incluirResumen: formData.incluirResumen,
                incluirGrafico: formData.incluirGrafico,
                orientacion: formData.orientacion
            };

            const payload = {
                id: editingPlantilla?.Id || 0,
                nombre: formData.nombre,
                descripcion: formData.descripcion,
                configuracion: JSON.stringify(configuracion),
                scope: formData.scope,
                categoria: formData.categoria || categoriaDefault || 'Judiciales'
            };

            const res = await plantillasGuardarJWT(payload);

            if (res?.ok) {
                setEditorOpen(false);
                openSnack(editingPlantilla ? 'Plantilla actualizada' : 'Plantilla creada', 'success');
                cargarPlantillas();
            } else {
                openSnack(res?.mensaje || 'Error al guardar', 'error');
            }
        } catch (error) {
            openSnack('Error: ' + error.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    // Abrir confirmación de eliminar
    const handleOpenDeleteConfirm = (plantilla) => {
        setPlantillaToDelete(plantilla);
        setDeleteConfirmOpen(true);
    };

    // Eliminar plantilla
    const handleEliminar = async () => {
        if (!plantillaToDelete) return;

        try {
            const res = await plantillasEliminarJWT(plantillaToDelete.Id);
            if (res?.ok) {
                setDeleteConfirmOpen(false);
                setPlantillaToDelete(null);
                openSnack('Plantilla eliminada', 'success');
                cargarPlantillas();
            } else {
                openSnack(res?.mensaje || 'Error al eliminar', 'error');
            }
        } catch (error) {
            openSnack('Error: ' + error.message, 'error');
        }
    };

    // Dejar de compartir conmigo
    const handleUnshareMe = async () => {
        if (!plantillaToUnshare) return;

        try {
            const res = await plantillasUnshareJWT(plantillaToUnshare.Id);

            if (res?.ok) {
                setUnshareConfirmOpen(false);
                openSnack('La plantilla ya no se mostrará en "Compartidas"', 'success');
                // Refrescamos lista
                cargarPlantillas();
                setPlantillaToUnshare(null);
            } else {
                openSnack(res?.mensaje || 'Error al dejar de compartir', 'error');
            }
        } catch (error) {
            openSnack('Error de red al dejar de compartir', 'error');
        }
    };


    // Toggle favorito
    const handleToggleFavorito = async (plantilla) => {
        try {
            const res = await plantillasFavoritoJWT(plantilla.Id, !plantilla.EsFavorito);
            if (res?.ok) {
                setPlantillas(prev => prev.map(p =>
                    p.Id === plantilla.Id ? { ...p, EsFavorito: !p.EsFavorito } : p
                ));
                openSnack(
                    plantilla.EsFavorito ? 'Quitado de favoritos' : 'Agregado a favoritos',
                    'success'
                );
            } else {
                openSnack(res?.mensaje || 'Error al actualizar favorito', 'error');
            }
        } catch (error) {
            openSnack('Error: ' + error.message, 'error');
        }
    };

    // Seleccionar plantilla para usar
    const handleSeleccionar = (plantilla) => {
        console.log('[PlantillasReportesManager] Seleccionar plantilla:', plantilla);

        let seleccionHecha = false;

        if (onSelect) {
            try {
                const config = JSON.parse(plantilla.Configuracion || '{}');
                onSelect({
                    ...plantilla,
                    configParsed: config
                });
                seleccionHecha = true;
            } catch (err) {
                console.warn('Error parseando Configuracion de plantilla:', err);
                onSelect(plantilla);
                seleccionHecha = true;
            }
        } else {
            console.warn('PlantillasReportesManager: onSelect NO está definido');
        }

        if (!seleccionHecha) {
            openSnack('No hay acción configurada para usar la plantilla.', 'warning');
        }

        if (onClose) onClose();
    };



    // Mover campo arriba/abajo
    const moverCampo = (index, direccion) => {
        const newCampos = [...formData.campos];
        const newIndex = index + direccion;
        if (newIndex < 0 || newIndex >= newCampos.length) return;

        [newCampos[index], newCampos[newIndex]] = [newCampos[newIndex], newCampos[index]];
        setFormData(prev => ({ ...prev, campos: newCampos }));
    };

    // Toggle campo
    const toggleCampo = (campoId) => {
        setFormData(prev => {
            const existe = prev.campos.includes(campoId);
            if (existe) {
                return { ...prev, campos: prev.campos.filter(c => c !== campoId) };
            } else {
                return { ...prev, campos: [...prev.campos, campoId] };
            }
        });
    };

    // ===== COMPARTIR =====
    const handleOpenShare = async (plantilla) => {
        setSharePlantilla(plantilla);
        setShareSelectedIds([]);
        setShareQuery('');
        setShareOpen(true);
        setShareLoading(true);

        try {
            const res = await plantillasCompartidosGetJWT(plantilla.Id);
            if (res?.ok) {
                const ids = Array.isArray(res.personalIds) ? res.personalIds.map(Number).filter(Number.isFinite) : [];
                setShareSelectedIds(ids);

                const next = { ...shareNameMap };
                if (Array.isArray(res.personas)) {
                    for (const p of res.personas) {
                        const pid = Number(p.id);
                        const nombre = String(p.nombre || "").trim();
                        if (Number.isFinite(pid) && nombre) next[pid] = nombre;
                    }
                }
                setShareNameMap(next);
            } else {
                openSnack(res?.mensaje || 'No se pudo cargar los usuarios compartidos', 'error');
            }
        } catch {
            openSnack('Error de red al cargar compartidos', 'error');
        } finally {
            setShareLoading(false);
        }
    };

    const handleSaveShare = async () => {
        if (!sharePlantilla) return;

        setShareSaving(true);
        try {
            const uniqueIds = Array.from(new Set((shareSelectedIds || []).map(Number).filter(Number.isFinite)));
            const payload = { id: sharePlantilla.Id, personalIds: uniqueIds };

            const res = await plantillasCompartidosSetJWT(payload.id, payload.personalIds);

            if (res?.ok) {
                setShareOpen(false);
                openSnack('Compartidos actualizados', 'success');
                cargarPlantillas();
            } else {
                openSnack(res?.mensaje || 'Error al guardar compartidos', 'error');
            }
        } catch (error) {
            openSnack('Error de red al guardar compartidos', 'error');
        } finally {
            setShareSaving(false);
        }
    };

    // Popper personalizado con z-index alto para que aparezca sobre el diálogo
    const CustomPopper = (props) => {
        return (
            <Popper
                {...props}
                style={{ zIndex: 9999 }}
                placement="bottom-start"
            />
        );
    };

    return (
        <>
            {/* Diálogo principal */}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                classes={{ paper: classes.dialogPaper }}
                style={{ zIndex: 1300 }}
            >
                <DialogTitle className={classes.header}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography style={{ fontWeight: 600, fontSize: 15 }}>
                            <span role="img" aria-label="plantillas">📊</span> Plantillas de Reportes
                        </Typography>
                        <IconButton onClick={onClose} style={{ color: 'white' }} size="large">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent style={{ padding: 0 }}>
                    {/* Tabs */}
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        <Tab
                            label="Públicas"
                            style={permisosPlantillas.puedeVerTodos ? {} : { display: 'none' }}
                        />
                        <Tab label="Mis Plantillas" />
                        <Tab label="Compartidas" />
                        <Tab label="Favoritas" />
                    </Tabs>


                    <Box p={2}>
                        {/* Barra de acciones */}
                        <Box display="flex" style={{ gap: 16 }} mb={2} alignItems="center">
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Buscar plantillas..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={{ flex: 1 }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleNueva}
                            >
                                Nueva
                            </Button>
                        </Box>

                        {/* Lista de plantillas */}
                        {loading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress />
                            </Box>
                        ) : plantillasFiltradas.length === 0 ? (
                            <Box textAlign="center" py={4}>
                                <Typography color="text.secondary">
                                    No hay plantillas disponibles
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                {plantillasFiltradas.map(plantilla => (
                                    <Paper key={plantilla.Id} className={classes.plantillaCard}>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                            <Box flex={1}>
                                                <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={0.5}>
                                                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                                        {plantilla.Nombre}
                                                    </Typography>
                                                    {plantilla.EsFavorito && (
                                                        <StarIcon style={{ fontSize: 16, color: '#ffd700' }} />
                                                    )}
                                                    {plantilla.Scope === 'GLOBAL' ? (
                                                        <Chip
                                                            icon={<PublicIcon style={{ fontSize: 12 }} />}
                                                            label="Global"
                                                            size="small"
                                                            style={{ fontSize: 10, height: 20, background: '#e3f2fd', color: '#1976d2' }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<LockIcon style={{ fontSize: 12 }} />}
                                                            label="Privado"
                                                            size="small"
                                                            style={{ fontSize: 10, height: 20, background: '#fff3e0', color: '#f57c00' }}
                                                        />
                                                    )}
                                                    {plantilla.Categoria && (
                                                        <Chip label={plantilla.Categoria} size="small" variant="outlined" style={{ fontSize: 10, height: 20 }} />
                                                    )}
                                                </Box>
                                                {plantilla.Descripcion && (
                                                    <Typography variant="body2" color="text.secondary" style={{ marginBottom: 8 }}>
                                                        {plantilla.Descripcion}
                                                    </Typography>
                                                )}
                                                <Box display="flex" style={{ gap: 4 }} flexWrap="wrap">
                                                    {(() => {
                                                        try {
                                                            const config = JSON.parse(plantilla.Configuracion || '{}');
                                                            return (config.campos || []).slice(0, 5).map(campoId => {
                                                                const campo = CAMPOS_DISPONIBLES.find(c => c.id === campoId);
                                                                return (
                                                                    <Chip
                                                                        key={campoId}
                                                                        label={campo?.label || campoId}
                                                                        size="small"
                                                                        style={{ fontSize: 10, height: 18 }}
                                                                    />
                                                                );
                                                            });
                                                        } catch {
                                                            return null;
                                                        }
                                                    })()}
                                                </Box>
                                            </Box>
                                            <Box display="flex" style={{ gap: 4 }}>
                                                <Tooltip title="Usar esta plantilla">
                                                    <IconButton size="small" onClick={() => handleSeleccionar(plantilla)}>
                                                        <PreviewIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>

                                                {puedeRealizarAccion(plantilla, 'favorite') && (
                                                    <Tooltip title={plantilla.EsFavorito ? "Quitar de favoritos" : "Agregar a favoritos"}>
                                                        <IconButton size="small" onClick={() => handleToggleFavorito(plantilla)}>
                                                            {plantilla.EsFavorito
                                                                ? <StarIcon style={{ color: '#ffd700' }} />
                                                                : <StarBorderIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {puedeRealizarAccion(plantilla, 'duplicate') && (
                                                    <Tooltip title="Duplicar">
                                                        <IconButton size="small" onClick={() => handleDuplicar(plantilla)}>
                                                            <CopyIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {puedeRealizarAccion(plantilla, 'share') && (
                                                    <Tooltip title="Compartir">
                                                        <IconButton size="small" onClick={() => handleOpenShare(plantilla)}>
                                                            <ShareIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {puedeRealizarAccion(plantilla, 'edit') && (
                                                    <Tooltip title="Editar">
                                                        <IconButton size="small" onClick={() => handleEditar(plantilla)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {puedeRealizarAccion(plantilla, 'delete') && (
                                                    <Tooltip title="Eliminar">
                                                        <IconButton size="small" onClick={() => handleOpenDeleteConfirm(plantilla)}>
                                                            <DeleteIcon color="error" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {puedeRealizarAccion(plantilla, 'unshare-self') && (
                                                    <Tooltip title="Dejar de compartir conmigo">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setPlantillaToUnshare(plantilla);
                                                                setUnshareConfirmOpen(true);
                                                            }}
                                                        >
                                                            <ExitToAppIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>

                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
            {/* Editor de plantilla */}
            <Dialog
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                maxWidth="lg"
                fullWidth
                style={{ zIndex: 1400 }}
            >
                <DialogTitle style={{ background: '#f5f5f5', paddingBottom: 12 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            {editingPlantilla ? 'Editar Plantilla' : 'Nueva Plantilla'}
                        </Typography>
                        {permisosPlantillas.puedeCrearGlobal && (
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Button
                                    variant={formData.scope === 'PRIVATE' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setFormData(prev => ({ ...prev, scope: 'PRIVATE' }))}
                                    startIcon={<LockIcon />}
                                    style={{
                                        borderRadius: 20,
                                        textTransform: 'none',
                                        ...(formData.scope === 'PRIVATE' ? {
                                            background: '#f57c00',
                                            color: 'white'
                                        } : {})
                                    }}
                                >
                                    Privado
                                </Button>
                                <Button
                                    variant={formData.scope === 'GLOBAL' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setFormData(prev => ({ ...prev, scope: 'GLOBAL' }))}
                                    startIcon={<PublicIcon />}
                                    style={{
                                        borderRadius: 20,
                                        textTransform: 'none',
                                        ...(formData.scope === 'GLOBAL' ? {
                                            background: '#1976d2',
                                            color: 'white'
                                        } : {})
                                    }}
                                >
                                    Global
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        {/* Columna izquierda */}
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
                                Información básica
                            </Typography>

                            <TextField
                                label="Nombre *"
                                fullWidth
                                size="small"
                                variant="outlined"
                                value={formData.nombre}
                                onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                style={{ marginBottom: 16 }}
                            />

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>Tipo de expediente</Typography>
                                <ToggleButtonGroup
                                    value={formData.categoria}
                                    exclusive
                                    onChange={(e, val) => { if (val) setFormData(prev => ({ ...prev, categoria: val })); }}
                                    size="small"
                                >
                                    <ToggleButton value="Judiciales" sx={{ textTransform: 'none', fontSize: 12 }}>Judiciales</ToggleButton>
                                    <ToggleButton value="Administrativos" sx={{ textTransform: 'none', fontSize: 12 }}>Administrativos</ToggleButton>
                                    <ToggleButton value="Ambos" sx={{ textTransform: 'none', fontSize: 12 }}>Ambos</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            <TextField
                                label="Descripción"
                                fullWidth
                                size="small"
                                variant="outlined"
                                multiline
                                rows={2}
                                value={formData.descripcion}
                                onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                                style={{ marginBottom: 16 }}
                            />

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>Orientación</Typography>
                                <ToggleButtonGroup
                                    value={formData.orientacion}
                                    exclusive
                                    onChange={(e, val) => { if (val) setFormData(prev => ({ ...prev, orientacion: val })); }}
                                    size="small"
                                >
                                    <ToggleButton value="portrait" sx={{ textTransform: 'none', fontSize: 12 }}>Vertical</ToggleButton>
                                    <ToggleButton value="landscape" sx={{ textTransform: 'none', fontSize: 12 }}>Horizontal</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.incluirResumen}
                                            onChange={e => setFormData(prev => ({ ...prev, incluirResumen: e.target.checked }))}
                                            color="primary"
                                        />
                                    }
                                    label="Incluir resumen estadístico"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.incluirGrafico}
                                            onChange={e => setFormData(prev => ({ ...prev, incluirGrafico: e.target.checked }))}
                                            color="primary"
                                        />
                                    }
                                    label="Incluir gráficos"
                                />
                            </Box>
                        </Grid>

                        {/* Columna derecha - Selector de campos */}
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
                                Campos del reporte ({formData.campos.length} seleccionados)
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Paper variant="outlined" style={{ padding: 12, height: 400, overflow: 'auto' }}>
                                        <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginBottom: 8 }}>
                                            Campos disponibles
                                        </Typography>
                                        {Object.entries(CAMPOS_POR_GRUPO).map(([grupo, campos]) => (
                                            <Box key={grupo}>
                                                <Typography className={classes.grupoHeader}>
                                                    {grupo}
                                                </Typography>
                                                {campos.map(campo => {
                                                    const seleccionado = formData.campos.includes(campo.id);
                                                    return (
                                                        <Box
                                                            key={campo.id}
                                                            className={`${classes.campoItem} ${seleccionado ? classes.campoItemSelected : ''}`}
                                                            onClick={() => toggleCampo(campo.id)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                <Typography variant="body2">
                                                                    {campo.label}
                                                                </Typography>
                                                                {seleccionado && (
                                                                    <Chip label="✓" size="small" color="primary" style={{ height: 16, fontSize: 10 }} />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        ))}
                                    </Paper>
                                </Grid>

                                <Grid size={6}>
                                    <Paper variant="outlined" style={{ padding: 12, height: 400, overflow: 'auto' }}>
                                        <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginBottom: 8 }}>
                                            Orden de columnas
                                        </Typography>
                                        {formData.campos.length === 0 ? (
                                            <Box textAlign="center" py={4}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Selecciona campos
                                                </Typography>
                                            </Box>
                                        ) : (
                                            formData.campos.map((campoId, index) => {
                                                const campo = CAMPOS_DISPONIBLES.find(c => c.id === campoId);
                                                return (
                                                    <Box
                                                        key={campoId}
                                                        className={classes.campoItem}
                                                        style={{ backgroundColor: '#e3f2fd' }}
                                                    >
                                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {index + 1}.
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {campo?.label || campoId}
                                                                </Typography>
                                                            </Box>
                                                            <Box display="flex" style={{ gap: 0 }}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => moverCampo(index, -1)}
                                                                    disabled={index === 0}
                                                                >
                                                                    <ArrowUpIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => moverCampo(index, 1)}
                                                                    disabled={index === formData.campos.length - 1}
                                                                >
                                                                    <ArrowDownIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => toggleCampo(campoId)}
                                                                >
                                                                    <DeleteIcon fontSize="small" color="error" />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                );
                                            })
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ padding: 16 }}>
                    <Button onClick={() => setEditorOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGuardar}
                        disabled={saving || !formData.nombre.trim() || formData.campos.length === 0}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Diálogo de confirmación de eliminación */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 1500 }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <DeleteIcon color="error" />
                        Confirmar eliminación
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de eliminar la plantilla "{plantillaToDelete?.Nombre}"?
                    </Typography>
                    <Alert severity="warning" style={{ marginTop: 16 }}>
                        Esta acción no se puede deshacer
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleEliminar}
                        color="secondary"
                        variant="contained"
                        startIcon={<DeleteIcon />}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Diálogo de compartir */}
            <Dialog
                open={shareOpen}
                onClose={() => !shareSaving && setShareOpen(false)}
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 1500 }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <ShareIcon color="primary" />
                            <Typography variant="h6">Compartir plantilla</Typography>
                        </Box>
                        <IconButton onClick={() => !shareSaving && setShareOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {sharePlantilla?.Nombre || ''}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {shareLoading ? (
                        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Typography variant="caption" color="text.secondary">
                                Buscá por nombre, apellido o usuario (min. 2 caracteres)
                            </Typography>

                                <Autocomplete
                                    multiple
                                    options={shareOptionsComputed}
                                    value={[]}
                                    renderTags={() => null}
                                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                                    getOptionLabel={(o) => o?.nombre || String(o?.id || "")}
                                    inputValue={shareQuery}
                                    onInputChange={(e, v, reason) => {
                                        if (reason !== "reset") setShareQuery(v || "");
                                    }}
                                    onChange={(e, newValue) => {
                                        const last = newValue && newValue.length ? newValue[newValue.length - 1] : null;
                                        if (!last) return;
                                        const pid = Number(last.id);
                                        if (!Number.isFinite(pid)) return;
                                        setShareSelectedIds(prev => prev.includes(pid) ? prev : [...prev, pid]);
                                        setShareNameMap(prev => ({ ...prev, [pid]: last.nombre || String(pid) }));
                                        setShareQuery("");
                                        setTimeout(() => {
                                            const input = document.querySelector('[placeholder*="Buscar por apellido"]');
                                            if (input) input.blur();
                                        }, 100);
                                    }}
                                    disableCloseOnSelect={false}
                                    selectOnFocus
                                    clearOnBlur
                                    handleHomeEndKeys
                                    filterSelectedOptions
                                    onClose={() => setShareQuery("")}
                                    PopperComponent={CustomPopper}
                                    filterOptions={(x) => x}
                                    renderOption={(props, opt) => (
                                        <li {...props} key={opt.id}>
                                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                                <PersonIcon fontSize="small" />
                                                <Typography variant="body2">{opt.nombre || `#${opt.id}`}</Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            size="small"
                                            placeholder="Buscar por apellido o nombre (Enter para agregar)"
                                            style={{ marginTop: 8 }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {shareSearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />

                            {shareSelectedIds.length === 0 ? (
                                <Alert severity="info" style={{ marginTop: 12 }}>
                                    Aún no está compartido con nadie.
                                </Alert>
                            ) : (
                                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }} mt={2}>
                                    {shareSelectedIds.map(id => (
                                        <Chip
                                            key={id}
                                            label={shareNameMap[id] || String(id)}
                                            onDelete={() => setShareSelectedIds(prev => prev.filter(x => x !== id))}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShareOpen(false)} disabled={shareSaving}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSaveShare}
                        color="primary"
                        variant="contained"
                        startIcon={shareSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        disabled={shareSaving}
                    >
                        {shareSaving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Diálogo de confirmación de dejar de compartir conmigo */}
            <Dialog
                open={unshareConfirmOpen}
                onClose={() => setUnshareConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 1500 }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <ExitToAppIcon color="primary" />
                        Dejar de ver esta plantilla
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Querés dejar de ver la plantilla "{plantillaToUnshare?.Nombre}" en la pestaña de compartidas?
                    </Typography>
                    <Alert severity="info" style={{ marginTop: 16 }}>
                        No se elimina la plantilla, solo deja de estar compartida con tu usuario.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnshareConfirmOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleUnshareMe}
                        color="primary"
                        variant="contained"
                        startIcon={<ExitToAppIcon />}
                    >
                        Dejar de ver
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Snackbar para mensajes */}
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={closeSnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={closeSnack} severity={snack.severity} variant="filled">
                    {snack.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PlantillasReportesManager;
